import is from '@sindresorhus/is';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
    JobArchitectureSearchItemWithDetail,
    JobArchitectureSearchResponseWithDetail,
} from '~/shared/data-contract/job-architecture-search';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import type { JobProfilesReviewerAssignmentFilter, JobProfilesTypeFilter } from './job-profile-management-filter-types';
import { jobArchitectureKeys } from './query-keys';

const PAGE_SIZE = 50;

export type JobProfileManagementV2Sort = {
    readonly direction: 'asc' | 'desc';
    readonly field: string;
};

type UseJobProfileManagementV2RolesOptions = {
    readonly architectureId: string | undefined;
    readonly reviewerAssignment?: JobProfilesReviewerAssignmentFilter;
    readonly reviewerId?: string;
    readonly searchTerm: string;
    readonly sort?: JobProfileManagementV2Sort;
    readonly type: readonly JobProfilesTypeFilter[];
};

export type JobProfileManagementV2Profile = JobArchitectureSearchItemWithDetail & {
    readonly level: 'level' | 'role';
};

const isProfile = (item: JobArchitectureSearchItemWithDetail): item is JobProfileManagementV2Profile =>
    item.level === 'level' || item.level === 'role';

/**
 * Roles and levels of the selected job architecture (the picker scopes the page to one architecture),
 * via the single-architecture `searchJobArchitecture` endpoint. Constrained to the requested profile
 * `type`s (role and/or level) plus free-text search, sorted, with infinite scroll.
 */
export const useJobProfileManagementV2Roles = ({
    architectureId,
    reviewerAssignment,
    reviewerId,
    searchTerm,
    sort,
    type,
}: UseJobProfileManagementV2RolesOptions): {
    readonly error: Error | null;
    readonly fetchNextPage: () => void;
    readonly hasNextPage: boolean;
    readonly isFetching: boolean;
    readonly isFetchingNextPage: boolean;
    readonly isLoading: boolean;
    readonly roles: readonly JobProfileManagementV2Profile[];
} => {
    const fetchers = useApplicationFetchers();
    const trimmedSearchTerm = searchTerm.trim();
    const queryKey = jobArchitectureKeys.jobProfileManagementProfiles(
        architectureId ?? '',
        trimmedSearchTerm,
        reviewerId,
        reviewerAssignment,
        type,
        sort,
    );

    const query = useInfiniteQuery<
        JobArchitectureSearchResponseWithDetail,
        Error,
        InfiniteData<JobArchitectureSearchResponseWithDetail, number>,
        ReturnType<typeof jobArchitectureKeys.jobProfileManagementProfiles>,
        number
    >({
        enabled: is.nonEmptyString(architectureId),
        getNextPageParam: (lastPage) =>
            lastPage.pagination.page + 1 < lastPage.pagination.pages ? lastPage.pagination.page + 1 : undefined,
        initialPageParam: 0,
        queryFn: async ({ pageParam }): Promise<JobArchitectureSearchResponseWithDetail> => {
            if (!is.nonEmptyString(architectureId)) {
                throw new Error('Missing job architecture id');
            }

            const result = await fetchers.searchJobArchitecture({
                detail: true,
                jobArchitectureId: architectureId,
                page: pageParam,
                searchTerm: trimmedSearchTerm,
                size: PAGE_SIZE,
                type,
                ...(reviewerAssignment !== undefined ? { reviewerAssignment } : {}),
                ...(reviewerId !== undefined ? { reviewerId } : {}),
                ...(sort !== undefined ? { sort } : {}),
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey,
        staleTime: 5000,
        throwOnError: false,
    });

    const roles = useMemo(() => (query.data?.pages ?? []).flatMap((page) => page.data.filter(isProfile)), [query.data]);

    return {
        error: query.error,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        hasNextPage: query.hasNextPage ?? false,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
        roles,
    };
};
